import React from 'react';
import { BaseStep, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import Checkbox from '../../../Checkbox';
import RangeSelector from '../RangeSelector';
import industryIcon from '../../../../assets/icons/industry.png';
import bizModelIcon from '../../../../assets/icons/biz_model.gif';
import productIcon from '../../../../assets/icons/product.png';
import stageIcon from '../../../../assets/icons/stage.gif';
import amountIcon from '../../../../assets/icons/amount.gif';
import { useProfile } from '../../../../contexts/ProfileContext';
import {
  industrySelectOptions,
  canonicalIndustryList,
  modelSelectOptions,
  productTypeSelectOptions,
  stageSelectOptions,
} from '../../../../constants/industry';
import {
  collapseBusinessModelPreference,
  expandBusinessModelPreference,
} from '../../../../utils/businessModelPreference';
import {
  MAX_INVESTMENT_AMOUNT_USD,
  MIN_INVESTMENT_AMOUNT,
} from '../../../../constants/profileFieldLimits';
import { unionStringLists } from '../../../../utils/unionStringLists';
import {
  industryRangeIsEmpty,
  investorInvestmentRangeFloor,
  investmentRangeIsSaveable,
} from '../../../../utils/investmentRangeEdit';
import { maxInvestmentInCurrency } from '../../../../utils/minStartupAsk';

const IndustryStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { investorProfile } = useProfile();

  // Filter out the "Select..." placeholder options
  const industriesOptions = industrySelectOptions.filter(opt => opt.value !== '');
  // Investor UI uses B2B + B2C checkboxes (both → b2b-b2c); exclude the combined option
  const businessModelOptions = modelSelectOptions.filter(
    opt => opt.value !== '' && opt.value !== 'b2b-b2c'
  );
  const productFocusOptions = productTypeSelectOptions.filter(opt => opt.value !== '');
  const stagesOptions = stageSelectOptions.filter(opt => opt.value !== '');

  // Initialize selected values from profile
  const [selectedIndustries, setSelectedIndustries] = React.useState<string[]>(() =>
    canonicalIndustryList(investorProfile?.industries || [])
  );
  // Express signup may store both as `b2b-b2c` — expand for B2B/B2C checkboxes
  const [selectedBusinessModels, setSelectedBusinessModels] = React.useState<string[]>(() =>
    expandBusinessModelPreference(investorProfile?.businessModelPreference)
  );
  const [selectedProductFocuses, setSelectedProductFocuses] = React.useState<string[]>(
    investorProfile?.productFocusPreference || []
  );
  const [selectedStages, setSelectedStages] = React.useState<string[]>(
    investorProfile?.stages || []
  );

  // Initialize investment range from profile (always true amounts).
  const getInitialRange = (): [number, number] => {
    if (!investorProfile?.investmentRange) {
      return [0, 0];
    }
    return investorProfile.investmentRange;
  };

  const [investmentRange, setInvestmentRange] = React.useState<[number, number]>(getInitialRange());
  const [rangeEdited, setRangeEdited] = React.useState(false);

  // Detect if multiplier should be active based on saved flag or amounts above ~20M
  const [isMultiplierActive, setIsMultiplierActive] = React.useState<boolean>(() => {
    const flag = investorProfile?.investmentMultiplierActive ?? false;
    const saved = investorProfile?.investmentRange;
    const lowCeiling = MAX_INVESTMENT_AMOUNT_USD / 10;
    const aboveCeiling = Array.isArray(saved) && (saved[0] > lowCeiling || saved[1] > lowCeiling);
    return flag || aboveCeiling;
  });

  const [currency, setCurrency] = React.useState<string>(investorProfile?.currency || 'usd');
  /** User edited after the first hydrate — do not overwrite their choices on later profile refreshes. */
  const preferencesTouchedRef = React.useRef(false);
  /** First successful load from Firestore (always runs, even if the user clicked early). */
  const hasHydratedRef = React.useRef(false);

  const { formData, setFormData } = useFormData({});

  React.useEffect(() => {
    if (!investorProfile) return;

    const applyRangeFromProfile = () => {
      const multiplierWasActive = investorProfile.investmentMultiplierActive ?? false;
      if (
        Array.isArray(investorProfile.investmentRange) &&
        investorProfile.investmentRange.length === 2
      ) {
        const [min, max] = investorProfile.investmentRange;
        setInvestmentRange([min, max]);
        // Unlock if the flag says so, or if saved amounts already exceed the default ~20M ceiling.
        const lowCeiling = MAX_INVESTMENT_AMOUNT_USD / 10;
        setIsMultiplierActive(multiplierWasActive || min > lowCeiling || max > lowCeiling);
      } else {
        setIsMultiplierActive(multiplierWasActive);
      }
    };

    // First hydrate must not be skipped: an early click used to set preferencesTouched
    // and block loading saved industries — Save then wiped Firestore down to the few
    // boxes visible in that session (e.g. only Fintech + a couple others).
    if (!hasHydratedRef.current) {
      setSelectedIndustries(prev =>
        canonicalIndustryList(unionStringLists(investorProfile.industries, prev))
      );
      setSelectedProductFocuses(prev =>
        unionStringLists(investorProfile.productFocusPreference, prev)
      );
      setSelectedStages(prev => unionStringLists(investorProfile.stages, prev));
      setSelectedBusinessModels(prev =>
        unionStringLists(
          expandBusinessModelPreference(investorProfile.businessModelPreference),
          prev
        )
      );
      setCurrency(investorProfile.currency || 'usd');
      applyRangeFromProfile();
      hasHydratedRef.current = true;
      return;
    }

    if (preferencesTouchedRef.current) return;
    setSelectedIndustries(canonicalIndustryList(investorProfile.industries || []));
    setSelectedProductFocuses(investorProfile.productFocusPreference || []);
    setSelectedStages(investorProfile.stages || []);
    setCurrency(investorProfile.currency || 'usd');
    applyRangeFromProfile();
  }, [investorProfile]);

  // Re-hydrate business model on later profile updates (first load handled above)
  const storedBusinessModelKey = JSON.stringify(investorProfile?.businessModelPreference ?? []);
  React.useEffect(() => {
    if (!hasHydratedRef.current || preferencesTouchedRef.current) return;
    const stored = JSON.parse(storedBusinessModelKey) as string[];
    const expanded = expandBusinessModelPreference(stored);
    setSelectedBusinessModels(prev => {
      const prevKey = [...prev].sort().join('|');
      const nextKey = [...expanded].sort().join('|');
      return prevKey === nextKey ? prev : expanded;
    });
  }, [storedBusinessModelKey]);

  // Handle industry selection
  const handleIndustryToggle = (industryValue: string) => {
    preferencesTouchedRef.current = true;
    setSelectedIndustries(prev => {
      if (prev.includes(industryValue)) {
        return prev.filter(v => v !== industryValue);
      }
      return [...prev, industryValue];
    });
  };

  // Handle business model selection
  const handleBusinessModelToggle = (modelValue: string) => {
    preferencesTouchedRef.current = true;
    setSelectedBusinessModels(prev => {
      if (prev.includes(modelValue)) {
        return prev.filter(v => v !== modelValue);
      }
      return [...prev, modelValue];
    });
  };

  // Handle product focus selection
  const handleProductFocusToggle = (focusValue: string) => {
    preferencesTouchedRef.current = true;
    setSelectedProductFocuses(prev => {
      if (prev.includes(focusValue)) {
        return prev.filter(v => v !== focusValue);
      }
      return [...prev, focusValue];
    });
  };

  // Handle stage selection
  const handleStageToggle = (stageValue: string) => {
    preferencesTouchedRef.current = true;
    setSelectedStages(prev => {
      if (prev.includes(stageValue)) {
        return prev.filter(v => v !== stageValue);
      }
      return [...prev, stageValue];
    });
  };

  // Select all industries
  const handleSelectAllIndustries = () => {
    preferencesTouchedRef.current = true;
    if (selectedIndustries.length === industriesOptions.length) {
      // Deselect all
      setSelectedIndustries([]);
    } else {
      // Select all
      setSelectedIndustries(industriesOptions.map(opt => opt.value));
    }
  };

  // Select all business models
  const handleSelectAllBusinessModels = () => {
    preferencesTouchedRef.current = true;
    if (selectedBusinessModels.length === businessModelOptions.length) {
      setSelectedBusinessModels([]);
    } else {
      setSelectedBusinessModels(businessModelOptions.map(opt => opt.value));
    }
  };

  // Select all product focuses
  const handleSelectAllProductFocuses = () => {
    preferencesTouchedRef.current = true;
    if (selectedProductFocuses.length === productFocusOptions.length) {
      setSelectedProductFocuses([]);
    } else {
      setSelectedProductFocuses(productFocusOptions.map(opt => opt.value));
    }
  };

  // Select all stages
  const handleSelectAllStages = () => {
    preferencesTouchedRef.current = true;
    if (selectedStages.length === stagesOptions.length) {
      setSelectedStages([]);
    } else {
      setSelectedStages(stagesOptions.map(opt => opt.value));
    }
  };

  // Update formData when selections change (range is already true amounts).
  React.useEffect(() => {
    const cap = maxInvestmentInCurrency(currency || 'usd', null, MAX_INVESTMENT_AMOUNT_USD);
    const finalInvestmentRange: [number, number] = [
      Math.min(cap, investmentRange[0]),
      Math.min(cap, investmentRange[1]),
    ];

    setFormData(prev => ({
      ...prev,
      industries: selectedIndustries,
      // Persist in Express signup shape: both → `b2b-b2c`
      businessModelPreference: collapseBusinessModelPreference(selectedBusinessModels),
      productFocusPreference: selectedProductFocuses,
      stages: selectedStages,
      investmentRange: finalInvestmentRange,
      investmentMultiplierActive: isMultiplierActive,
      currency: currency,
    }));
  }, [
    selectedIndustries,
    selectedBusinessModels,
    selectedProductFocuses,
    selectedStages,
    investmentRange,
    isMultiplierActive,
    currency,
    setFormData,
  ]);

  const investmentPayload = React.useCallback(() => {
    const cap = maxInvestmentInCurrency(currency || 'usd', null, MAX_INVESTMENT_AMOUNT_USD);
    const finalInvestmentRange: [number, number] = [
      Math.min(cap, investmentRange[0]),
      Math.min(cap, investmentRange[1]),
    ];
    return {
      industries: selectedIndustries,
      businessModelPreference: collapseBusinessModelPreference(selectedBusinessModels),
      productFocusPreference: selectedProductFocuses,
      stages: selectedStages,
      investmentRange: finalInvestmentRange,
      investmentMultiplierActive: isMultiplierActive,
      currency,
    };
  }, [
    currency,
    investmentRange,
    isMultiplierActive,
    selectedBusinessModels,
    selectedIndustries,
    selectedProductFocuses,
    selectedStages,
  ]);

  // Track if we need to show validation errors
  const [showErrors, setShowErrors] = React.useState(false);
  const rangeFloor = investorInvestmentRangeFloor(currency);
  const rangeIsValid = investmentRangeIsSaveable(investmentRange, rangeFloor);

  // Listen for validation requests from parent
  React.useEffect(() => {
    if (shouldValidate) {
      // Validation: at least one selection in each category; range must meet the currency floor.
      const isValid =
        selectedIndustries.length > 0 &&
        selectedBusinessModels.length > 0 &&
        selectedProductFocuses.length > 0 &&
        selectedStages.length > 0 &&
        rangeIsValid;

      setShowErrors(true);

      // Notify parent about validation result
      if (onValidationResult) {
        onValidationResult(isValid, investmentPayload());
      }

      // Notify parent that validation was handled
      if (onValidationHandled) {
        onValidationHandled();
      }
    }
  }, [
    shouldValidate,
    selectedIndustries,
    selectedBusinessModels,
    selectedProductFocuses,
    selectedStages,
    rangeIsValid,
    investmentPayload,
    onValidationHandled,
    onValidationResult,
  ]);

  // Continuously validate when errors are shown and notify parent when validation passes
  React.useEffect(() => {
    if (showErrors) {
      const isValid =
        selectedIndustries.length > 0 &&
        selectedBusinessModels.length > 0 &&
        selectedProductFocuses.length > 0 &&
        selectedStages.length > 0 &&
        rangeIsValid;

      // Notify parent when validation passes (user fixed errors)
      if (isValid && onValidationResult) {
        onValidationResult(true, investmentPayload());
      }
    }
  }, [
    showErrors,
    selectedIndustries,
    selectedBusinessModels,
    selectedProductFocuses,
    selectedStages,
    rangeIsValid,
    investmentPayload,
    onValidationResult,
  ]);

  // Expose form data to parent component
  React.useEffect(() => {
    if (onCreateProfile) {
      onCreateProfile(formData);
    }
  }, [formData, onCreateProfile]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      {/* Industries Section */}
      <div className="space-y-3 sm:space-y-5">
        {/* Section Header with SELECT ALL */}
        <div className="flex items-end space-x-2 sm:space-x-3 pl-3 sm:pl-0">
          <div className="flex items-end space-x-2 sm:space-x-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0 mr-1 sm:mr-2 md:mr-4">
              <img
                src={industryIcon}
                alt="INDUSTRIES icon"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-sm sm:text-base md:text-lg font-bold uppercase text-white">
              INDUSTRIES
            </h3>
          </div>
          <button
            type="button"
            onClick={handleSelectAllIndustries}
            className="text-white text-[10px] sm:text-xs hover:text-emerald-400 transition-colors ml-2 sm:ml-4 flex-shrink-0"
          >
            {selectedIndustries.length === industriesOptions.length ? 'UNSELECT ALL' : 'SELECT ALL'}
          </button>
        </div>

        {/* Section Content */}
        <div className="space-y-2 sm:space-y-4 px-3 sm:pl-10 md:pl-12">
          <div className="space-y-2 sm:space-y-3">
            {industriesOptions.map(option => (
              <Checkbox
                key={option.value}
                id={`industry-${option.value}`}
                checked={selectedIndustries.includes(option.value)}
                onChange={() => handleIndustryToggle(option.value)}
                label={option.name}
                align="center"
              />
            ))}
          </div>
          {showErrors && selectedIndustries.length === 0 && (
            <p className="text-red-500 text-sm mt-2 text-right">
              Please select at least one industry
            </p>
          )}
        </div>
      </div>

      {/* Business Model Preference Section */}
      <div className="space-y-3 sm:space-y-5">
        {/* Section Header with SELECT ALL */}
        <div className="flex items-end justify-between space-x-2 sm:space-x-3 pl-3 sm:pl-0">
          <div className="flex items-end space-x-2 sm:space-x-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0 mr-1 sm:mr-2 md:mr-4">
              <img
                src={bizModelIcon}
                alt="Business model icon"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-sm sm:text-base md:text-lg font-bold uppercase text-white">
              Business model preference
            </h3>
          </div>
          <button
            type="button"
            onClick={handleSelectAllBusinessModels}
            className="text-white text-[10px] sm:text-xs hover:text-emerald-400 transition-colors ml-2 sm:ml-4 flex-shrink-0"
          >
            {selectedBusinessModels.length === businessModelOptions.length
              ? 'UNSELECT ALL'
              : 'SELECT ALL'}
          </button>
        </div>

        {/* Section Content */}
        <div className="space-y-2 sm:space-y-3 px-3 sm:pl-10 md:pl-12">
          {businessModelOptions.map(option => (
            <Checkbox
              key={option.value}
              id={`business-model-${option.value}`}
              checked={selectedBusinessModels.includes(option.value)}
              onChange={() => handleBusinessModelToggle(option.value)}
              label={option.name}
              align="center"
            />
          ))}
        </div>
        {showErrors && selectedBusinessModels.length === 0 && (
          <p className="text-red-500 text-sm mt-2 text-right pl-10 sm:pl-14 md:pl-16">
            Please select at least one business model
          </p>
        )}
      </div>

      {/* Product Focus Preference Section */}
      <div className="space-y-3 sm:space-y-5">
        {/* Section Header with SELECT ALL */}
        <div className="flex items-end justify-between space-x-2 sm:space-x-3 pl-3 sm:pl-0">
          <div className="flex items-end space-x-2 sm:space-x-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0 mr-1 sm:mr-2 md:mr-4">
              <img
                src={productIcon}
                alt="Product focus icon"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-sm sm:text-base md:text-lg font-bold uppercase text-white">
              Product focus preference
            </h3>
          </div>
          <button
            type="button"
            onClick={handleSelectAllProductFocuses}
            className="text-white text-[10px] sm:text-xs hover:text-emerald-400 transition-colors ml-2 sm:ml-4 flex-shrink-0"
          >
            {selectedProductFocuses.length === productFocusOptions.length
              ? 'UNSELECT ALL'
              : 'SELECT ALL'}
          </button>
        </div>

        {/* Section Content */}
        <div className="space-y-2 sm:space-y-3 px-3 sm:pl-10 md:pl-12">
          {productFocusOptions.map(option => (
            <Checkbox
              key={option.value}
              id={`product-focus-${option.value}`}
              checked={selectedProductFocuses.includes(option.value)}
              onChange={() => handleProductFocusToggle(option.value)}
              label={option.name}
              align="center"
            />
          ))}
        </div>
        {showErrors && selectedProductFocuses.length === 0 && (
          <p className="text-red-500 text-sm mt-2 text-right pl-10 sm:pl-14 md:pl-16">
            Please select at least one product focus
          </p>
        )}
      </div>

      {/* Stages Section */}
      <div className="space-y-3 sm:space-y-5">
        {/* Section Header with SELECT ALL */}
        <div className="flex items-end justify-between space-x-2 sm:space-x-3 pl-3 sm:pl-0">
          <div className="flex items-end space-x-2 sm:space-x-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0 mr-1 sm:mr-2 md:mr-4">
              <img src={stageIcon} alt="Stages icon" className="w-full h-full object-contain" />
            </div>
            <h3 className="text-sm sm:text-base md:text-lg font-bold uppercase text-white">
              Stages
            </h3>
          </div>
          <button
            type="button"
            onClick={handleSelectAllStages}
            className="text-white text-[10px] sm:text-xs hover:text-emerald-400 transition-colors ml-2 sm:ml-4 flex-shrink-0"
          >
            {selectedStages.length === stagesOptions.length ? 'UNSELECT ALL' : 'SELECT ALL'}
          </button>
        </div>

        {/* Section Content */}
        <div className="space-y-2 sm:space-y-3 px-3 sm:pl-10 md:pl-12">
          {stagesOptions.map(option => (
            <Checkbox
              key={option.value}
              id={`stage-${option.value}`}
              checked={selectedStages.includes(option.value)}
              onChange={() => handleStageToggle(option.value)}
              label={option.name}
              align="center"
            />
          ))}
        </div>
        {showErrors && selectedStages.length === 0 && (
          <p className="text-red-500 text-sm mt-2 text-right pl-10 sm:pl-14 md:pl-16">
            Please select at least one stage
          </p>
        )}
      </div>

      {/* Investment Range Section */}
      <FormSection icon={amountIcon} title="INVESTMENT RANGE">
        <RangeSelector
          currency={currency}
          range={investmentRange}
          onCurrencyChange={next => {
            preferencesTouchedRef.current = true;
            setCurrency(next);
          }}
          onRangeChange={next => {
            preferencesTouchedRef.current = true;
            setRangeEdited(true);
            setInvestmentRange(next);
          }}
          isEmpty={industryRangeIsEmpty(rangeEdited, investmentRange)}
          multiplier={10}
          isMultiplierActive={isMultiplierActive}
          onMultiplierToggle={next => {
            preferencesTouchedRef.current = true;
            setIsMultiplierActive(next);
          }}
          min={MIN_INVESTMENT_AMOUNT}
          max={MAX_INVESTMENT_AMOUNT_USD}
          step={10000}
          scaleExponent={0.4}
        />
        {showErrors && !rangeIsValid && (
          <p className="text-red-500 text-sm mt-2 text-right">
            {investmentRangeIsSaveable(investmentRange)
              ? `Minimum investment: ${rangeFloor.toLocaleString()} ${currency.toUpperCase()}`
              : 'Please select a valid investment range'}
          </p>
        )}
      </FormSection>
    </BaseStep>
  );
};

export default IndustryStep;
